/**
 * Version Check Utility
 * Checks if the app version has changed and forces cache refresh
 */

const VERSION_KEY = 'app_version';
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes

/**
 * Get the current app version from the build
 */
export const getCurrentVersion = () => {
  return import.meta.env.VITE_APP_VERSION || 'dev';
};

/**
 * Get the build timestamp
 */
export const getBuildTime = () => {
  return import.meta.env.VITE_BUILD_TIME || Date.now();
};

/**
 * Check if a new version is available
 */
export const checkVersion = async () => {
  try {
    // Fetch version.json with cache busting
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    
    if (!response.ok) {
      // Silently fail if version.json doesn't exist
      return false;
    }

    // Check if response is actually JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      // Silently fail if not JSON (e.g., HTML 404 page)
      return false;
    }

    const { version: serverVersion, buildTime: serverBuildTime } = await response.json();
    const currentVersion = getCurrentVersion();
    const currentBuildTime = getBuildTime();

    // Compare versions - only check version number, not build time
    // Build times can differ slightly even for the same version
    if (serverVersion !== currentVersion) {
      console.log('New version detected:', {
        current: currentVersion,
        server: serverVersion,
        currentBuildTime,
        serverBuildTime
      });
      return true;
    }

    return false;
  } catch (error) {
    // Silently fail - version checking is optional
    // Only log if it's not a JSON parse error (which happens when version.json doesn't exist)
    if (!error.message?.includes('JSON')) {
      console.warn('Version check failed:', error.message);
    }
    return false;
  }
};

/**
 * Force refresh the app to get the latest version
 */
export const forceRefresh = () => {
  console.log('Forcing app refresh for new version...');
  
  // Clear all caches
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => {
        caches.delete(name);
      });
    });
  }

  // Clear local storage version
  localStorage.removeItem(VERSION_KEY);
  
  // Hard reload
  window.location.reload(true);
};

/**
 * Initialize version checking
 * @param {Function} onNewVersionCallback - Optional callback when new version is detected
 */
export const initVersionCheck = (onNewVersionCallback) => {
  const currentVersion = getCurrentVersion();
  const storedVersion = localStorage.getItem(VERSION_KEY);

  console.log('App Version:', currentVersion);
  console.log('Build Time:', new Date(parseInt(getBuildTime())).toLocaleString());

  // If stored version doesn't match, we just loaded a new version
  if (storedVersion && storedVersion !== currentVersion) {
    console.log('Version updated from', storedVersion, 'to', currentVersion);
  }

  // Store current version
  localStorage.setItem(VERSION_KEY, currentVersion);

  // Check immediately on init
  checkVersion();

  // Set up periodic version checking (only if callback provided)
  if (onNewVersionCallback) {
    setInterval(async () => {
      const newVersionAvailable = await checkVersion();
      if (newVersionAvailable) {
        // Notify the app that a new version is available
        onNewVersionCallback();
      }
    }, VERSION_CHECK_INTERVAL);
  }
};

/**
 * Check version immediately (useful for manual checks)
 * @param {Function} onNewVersionCallback - Called when a new version is detected
 * @returns {Promise<boolean>} - True if new version is available
 */
export const checkForUpdates = async (onNewVersionCallback) => {
  const newVersionAvailable = await checkVersion();
  
  if (newVersionAvailable && onNewVersionCallback) {
    onNewVersionCallback();
  } else if (!newVersionAvailable) {
    console.log('You are running the latest version');
  }
  
  return newVersionAvailable;
};
